"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface LoadingContextType {
  isLoading: boolean;
  loadingText: string;
  showLoader: (text?: string) => void;
  hideLoader: () => void;
  setLoadingText: (text: string) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");

  const showLoader = useCallback((text?: string) => {
    setIsLoading(true);
    if (text) {
      setLoadingText(text);
    }
  }, []);

  const hideLoader = useCallback(() => {
    setIsLoading(false);
    setLoadingText("Loading...");
  }, []);

  const setLoadingTextHandler = useCallback((text: string) => {
    setLoadingText(text);
  }, []);

  const value: LoadingContextType = {
    isLoading,
    loadingText,
    showLoader,
    hideLoader,
    setLoadingText: setLoadingTextHandler,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
}; 